import React, { Fragment, useMemo } from 'react'
import { ReceiptWithCubeTest } from '../../containers/Sampling'
import { Family, ResourceType } from '../../types'
import ReportConsistency from './ReportConsistency'
import ReportPressureStrength from './ReportPressureStrength'
import ReportWCF from './ReportWCF'
import moment from 'moment'

export type FamilyWithReceipts = Omit<Family, 'id'> & { id: number | string, receipts: ReceiptWithCubeTest[] }

const EN206: React.FC<{ receipts: ReceiptWithCubeTest[], showConsistency: boolean, showWCF: boolean, showDeviations: boolean, startDate: Date, endDate: Date }> = ({ receipts, showConsistency, showWCF, showDeviations, startDate, endDate }) => {
  const families = useMemo(() => {
    const filtered = receipts.filter(receipt => receipt.cubeTest.cubes.filter(cube => cube.numberOfDays && cube.numberOfDays === 28 && cube.pressureStrength && cube.pressureStrength > 0).length > 0)
    return filtered.reduce((families: FamilyWithReceipts[], receipt) => {
      let recipeFamilies: Array<Omit<Family, 'id'> & { id: number | string }> = receipt.revision.recipe.families || []
      if ((!receipt.revision.recipe.families || receipt.revision.recipe.families.length === 0) && receipt.revision.recipe.strengthClass && (receipt.revision.recipe.environmentClasses || []).length > 0) {
        const maxWbf = (receipt.revision.recipe.environmentClasses || []).reduce((max, ec) => ec.maxWbf < max ? ec.maxWbf : max, 1)
        const binders = receipt.revision.recipe.ingredients.filter(({ resource }) => resource.type === ResourceType.Cement || resource.type === ResourceType.Filler).map(({ resource }) => resource).sort((a, b) => a.id - b.id)
        recipeFamilies = [{ id: `${receipt.revision.recipe.strengthClass.id}|${maxWbf}|${binders.map(({ id }) => id).join(',')}`, name: receipt.revision.recipe.strengthClass.code + ' ' + (maxWbf + '').replace('.', ',') }]
      }
      const familyIds = families.map(f => f.id + '')
      const missing = recipeFamilies.filter(family => familyIds.indexOf(family.id + '') < 0)
      const existing = recipeFamilies.filter(family => familyIds.indexOf(family.id + '') >= 0)
      existing.forEach(family => {
        const index = families.findIndex(f => f.id === family.id)
        families[index].receipts.push(receipt)
      })
      missing.forEach(family => {
        families.push({ ...family, receipts: [receipt] })
      })
      return [...families]
    }, []).sort((a, b) => (a.receipts[0].revision.recipe.strengthClass ? a.receipts[0].revision.recipe.strengthClass?.cubePressureStrength : 0) - (b.receipts[0].revision.recipe.strengthClass ? b.receipts[0].revision.recipe.strengthClass?.cubePressureStrength : 0))
  }, [receipts])

  const filtered = useMemo(() => {
    return receipts.filter(r => r.cubeTest && moment(r.cubeTest.sampleDate).isSameOrAfter(startDate) && moment(r.cubeTest.sampleDate).isSameOrBefore(endDate))
  }, [receipts, startDate, endDate])

  return <Fragment>
    {families.map(family => <ReportPressureStrength family={family} key={family.id} />)}
    {showConsistency && <ReportConsistency receipts={filtered} showDeviations={showDeviations} />}
    {showWCF && <ReportWCF receipts={filtered} showDeviations={showDeviations} />}
  </Fragment>
}

export default EN206
